import { Cloudinary } from '@cloudinary/url-gen';
import { scale } from '@cloudinary/url-gen/actions/resize';
import { quality } from '@cloudinary/url-gen/actions/delivery';
import { format } from '@cloudinary/url-gen/actions/delivery';
import { autoBest } from '@cloudinary/url-gen/qualifiers/quality';
import { auto } from '@cloudinary/url-gen/qualifiers/format';
// import { autoGravity } from '@cloudinary/url-gen/qualifiers/gravity'; // Commented: unused import

class CloudinaryService {
  constructor() {
    this.cloudinary = new Cloudinary({
      cloud: {
        cloudName: 'dof0vdtf3' // מהמידע שסיפקת
      }
    });
    
    // Remove upload preset - we'll use signed uploads through our server
    this.baseApiUrl = `https://api.cloudinary.com/v1_1/dof0vdtf3`;
  }

  /**
   * יצירת מזהה ייעודי לתמונה
   * @param {string} gameId - מזהה המשחק
   * @param {string} type - סוג התמונה (layer/container/token)
   * @param {string} itemId - מזהה הפריט
   * @returns {string} מזהה ייעודי
   */
  generateImageId(gameId, type, itemId) {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substr(2, 6);
    return `${type}_${itemId}_${timestamp}_${randomSuffix}`;
  }

  /**
   * יצירת מזהה ייעודי למצב משחק
   * @param {string} gameId - מזהה המשחק
   * @param {string} gameName - שם המשחק
   * @returns {string} מזהה ייעודי
   */
  generateGameStateId(gameId, gameName) {
    const timestamp = Date.now();
    const cleanGameName = gameName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const randomSuffix = Math.random().toString(36).substr(2, 6);
    return `gamestate_${cleanGameName}_${timestamp}_${randomSuffix}`;
  }

  /**
   * העלאת תמונה ל-Cloudinary עם ארגון באלבומים ואופטימיזציה
   * @param {File|Blob|string} imageData - נתוני התמונה
   * @param {string} gameId - מזהה המשחק
   * @param {string} type - סוג התמונה (layer/container/token)
   * @param {string} itemId - מזהה הפריט
   * @param {Object} options - אפשרויות נוספות
   * @returns {Promise<Object>} תוצאת ההעלאה
   */
  async uploadImage(imageData, gameId, type = 'general', itemId = null, options = {}) {
    try {
      const formData = new FormData();
      
      // אם זה base64 string, נמיר ל-blob
      if (typeof imageData === 'string' && imageData.startsWith('data:')) {
        const response = await fetch(imageData);
        const blob = await response.blob();
        formData.append('file', blob);
      } else {
        formData.append('file', imageData);
      }
      
      // יצירת מזהה ייעודי
      const publicId = this.generateImageId(gameId, type, itemId || 'item');
      
      // הגדרת תיקיית היעד - אלבום לפי משחק
      const folder = `dnd_maps/games/${gameId}`;
      
      formData.append('folder', folder);
      formData.append('public_id', publicId);
      
      // הוספת tags לארגון טוב יותר
      const tags = [`game_${gameId}`, `type_${type}`, 'dnd_map_image'];
      if (itemId) {
        tags.push(`item_${itemId}`);
      }
      formData.append('tags', tags.join(','));
      
      // שליחה דרך השרת שלנו לחתימה עם אופטימיזציה
      const response = await fetch('/api/game-states/cloudinary/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      
      // 🔧 תיקון: נקה את ה-public_id שחוזר מהשרת כדי לוודא consistency
      let cleanPublicId = result.public_id;
      const expectedFolder = `dnd_maps/games/${gameId}/`;
      
      // הסרת כל המופעים של folder prefix מהתוצאה שחוזרת
      let previousLength = 0;
      while (cleanPublicId.includes(expectedFolder) && cleanPublicId.length !== previousLength) {
        previousLength = cleanPublicId.length;
        cleanPublicId = cleanPublicId.replace(expectedFolder, '');
      }
      
      // הסרת גם prefix של dnd_maps כללי אם נשאר
      if (cleanPublicId.startsWith('dnd_maps/')) {
        cleanPublicId = cleanPublicId.replace('dnd_maps/', '');
      }
      
      // הסרת slashes מיותרים בתחילת הstring
      cleanPublicId = cleanPublicId.replace(/^\/+/, '');
      
      // ודא שהתוצאה מתחילה עם game_ או בצורה תקינה
      if (cleanPublicId.startsWith('container_') || cleanPublicId.startsWith('token_') || cleanPublicId.includes(`${type}_`)) {
        // Successfully cleaned
      } else {
        console.warn(`[CloudinaryService] Could not clean public_id properly, using original: ${result.public_id}`);
        cleanPublicId = result.public_id; // השתמש בoriginal כfallback
      }
      
      // 🔧 תיקון קריטי: השתמש בpublic_id המקורי המלא ליצירת optimizedUrl
      // כדי שCloudinary SDK יבנה את הURL הנכון עם הנתיב המלא
      const optimizedUrl = this.getOptimizedImageUrl(result.public_id, {
        maxWidth: 1600,
        quality: 'auto:best',
        format: 'auto'
      });
      
      return {
        success: true,
        url: result.secure_url,
        optimizedUrl: optimizedUrl,
        publicId: cleanPublicId, // השתמש בpublic_id הנקי
        cloudinaryId: publicId, // המזהה הייעודי שיצרנו
        width: result.width,
        height: result.height,
        format: result.format,
        bytes: result.bytes,
        folder: folder,
        tags: tags
      };
      
    } catch (error) {
      console.error('Error uploading image:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * העלאת מצב משחק כ-JSON ל-Cloudinary עם ארגון באלבום
   * @param {Object} gameStateData - נתוני מצב המשחק
   * @param {string} gameId - מזהה המשחק
   * @param {string} gameName - שם המשחק
   * @param {string} existingPublicId - מזהה קיים לעדכון (אופציונלי)
   * @returns {Promise<Object>} תוצאת ההעלאה
   */
  async uploadGameState(gameStateData, gameId, gameName, existingPublicId = null) {
    try {
      // יצירת JSON string
      const jsonString = JSON.stringify(gameStateData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      const formData = new FormData();
      formData.append('file', blob);
      
      // יצירת מזהה ייעודי או שימוש בקיים
      let publicId = existingPublicId || this.generateGameStateId(gameId, gameName);
      
      // 🔧 תיקון מתקדם: הסרת כל folder prefixes מ-existing public_id כדי למנוע כפילות
      if (existingPublicId) {
        let cleanedId = existingPublicId;
        const folderPrefix = `dnd_maps/games/${gameId}/`;
        
        // הסרת כל המופעים של folder prefix (עם לולאה מתקדמת)
        let previousLength = 0;
        while (cleanedId.includes(folderPrefix) && cleanedId.length !== previousLength) {
          previousLength = cleanedId.length;
          cleanedId = cleanedId.replace(folderPrefix, '');
        }
        
        // הסרת גם prefix של dnd_maps כללי אם נשאר
        if (cleanedId.startsWith('dnd_maps/')) {
          cleanedId = cleanedId.replace('dnd_maps/', '');
        }
        
        // הסרת slashes מיותרים בתחילת הstring
        cleanedId = cleanedId.replace(/^\/+/, '');
        
        // ודא שהתוצאה מתחילה עם gamestate_ או בצורה תקינה
        if (cleanedId.startsWith('gamestate_')) {
          publicId = cleanedId;

        } else {
          console.warn(`[CloudinaryService] Could not clean public_id properly, using original: ${existingPublicId}`);
          publicId = existingPublicId; // השתמש בoriginal כfallback
        }
      }
      
      // הגדרת תיקיית היעד - אלבום לפי משחק
      const folder = `dnd_maps/games/${gameId}`;
      
      formData.append('folder', folder);
      formData.append('public_id', publicId);
      formData.append('resource_type', 'raw');
      
      // הוספת tags
      const tags = [`game_${gameId}`, 'gamestate', 'dnd_map_data'];
      formData.append('tags', tags.join(','));
      
      // שליחה דרך השרת שלנו לחתימה
      const { default: authInterceptor } = await import('./AuthInterceptor');
      
      const response = await authInterceptor.authenticatedFetch('/api/game-states/cloudinary/upload-gamestate', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Game state upload failed: ${response.statusText} - ${errorText}`);
      }
      
      const result = await response.json();
      
      // 🔧 תיקון מתקדם: ודא שה-publicId שחוזר הוא נקי מfolder duplicates
      let cleanPublicId = result.public_id;
      const expectedFolder = `dnd_maps/games/${gameId}/`;
      
      // הסרת כל המופעים של folder prefix מהתוצאה שחוזרת
      let previousLength = 0;
      while (cleanPublicId.includes(expectedFolder) && cleanPublicId.length !== previousLength) {
        previousLength = cleanPublicId.length;
        cleanPublicId = cleanPublicId.replace(expectedFolder, '');
      }
      
      // הסרת גם prefix של dnd_maps כללי אם נשאר
      if (cleanPublicId.startsWith('dnd_maps/')) {
        cleanPublicId = cleanPublicId.replace('dnd_maps/', '');
      }
      
      // הסרת slashes מיותרים בתחילת הstring
      cleanPublicId = cleanPublicId.replace(/^\/+/, '');
      
      // ודא שהתוצאה תקינה
      if (cleanPublicId && cleanPublicId.startsWith('gamestate_')) {
        // Successfully cleaned
      } else {
        console.warn(`[CloudinaryService] Could not clean returned public_id, using original: ${result.public_id}`);
        cleanPublicId = result.public_id; // השתמש בoriginal כfallback
      }
      
      return {
        success: true,
        url: result.secure_url,
        publicId: cleanPublicId, // השתמש בגרסה הנקיה
        cloudinaryId: publicId, // המזהה הייעודי שיצרנו
        bytes: result.bytes,
        folder: folder,
        tags: tags
      };
      
    } catch (error) {
      console.error('Error uploading game state:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * הורדת מצב משחק מ-Cloudinary
   * @param {string} gameStateUrl - URL של מצב המשחק
   * @returns {Promise<Object>} תוצאת ההורדה
   */
  async downloadGameState(gameStateUrl) {
    try {
      if (!gameStateUrl) {
        throw new Error('Game state URL is undefined or empty');
      }

      const response = await fetch(gameStateUrl);
      
      if (!response.ok) {
        throw new Error(`Failed to download game state: ${response.status} ${response.statusText}`);
      }
      
      const gameState = await response.json();
      
      return {
        success: true,
        data: gameState
      };
    } catch (error) {
      console.error('[CloudinaryService] Error downloading game state:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * מחיקת תמונה או קובץ מ-Cloudinary
   * @param {string} publicId - מזהה ציבורי של הקובץ
   * @param {string} resourceType - סוג המשאב (image/raw)
   * @returns {Promise<Object>} תוצאת המחיקה
   */
  async deleteFile(publicId, resourceType = 'image') {
    try {
      const response = await fetch(`/api/game-states/cloudinary/${encodeURIComponent(publicId)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          resourceType
        })
      });
      
      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }
      
      const result = await response.json();
      return {
        success: true,
        result
      };
      
    } catch (error) {
      console.error('Error deleting file:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * יצירת URL מותאם לתמונה עם אופטימיזציה מתקדמת
   * @param {string} publicId - מזהה ציבורי של התמונה
   * @param {Object} options - אפשרויות טרנספורמציה
   * @returns {string} URL מותאם
   */
  getOptimizedImageUrl(publicId, options = {}) {
    // ולידציה של ה-public ID
    if (!this.isValidPublicId(publicId)) {
      console.warn('Invalid public ID for optimization:', publicId);
      return null;
    }
    
    const {
      maxWidth = 2000,
      maxHeight = null,
      quality: qualityOption = 'auto:best',
      format: formatOption = 'auto',
      crop = 'limit'
    } = options;
    
    try {
      // יצירת אובייקט התמונה
      let img = this.cloudinary.image(publicId);
      
      // הוספת אופטימיזציה של גודל
      if (maxWidth || maxHeight) {
        const resizeOptions = { crop };
        if (maxWidth) resizeOptions.width = maxWidth;
        if (maxHeight) resizeOptions.height = maxHeight;
        
        img = img.resize(scale().width(maxWidth));
      }
      
      // הוספת אופטימיזציה של איכות
      if (qualityOption === 'auto:best') {
        img = img.delivery(quality(autoBest()));
      } else {
        img = img.delivery(quality(qualityOption));
      }
      
      // הוספת אופטימיזציה של פורמט
      if (formatOption === 'auto') {
        img = img.delivery(format(auto()));
      } else {
        img = img.delivery(format(formatOption));
      }
      
      const optimizedUrl = img.toURL();
      
      // ולידציה של ה-URL הסופי
      if (!optimizedUrl || optimizedUrl.includes('undefined') || optimizedUrl.includes('null')) {
        console.warn('Generated invalid optimized URL:', optimizedUrl, 'for publicId:', publicId);
        return null;
      }
      
      return optimizedUrl;
      
    } catch (error) {
      console.error('Error generating optimized URL for publicId:', publicId, error);
      return null;
    }
  }

  /**
   * ולידציה שה-public ID נראה תקין
   * @param {string} publicId - ה-public ID לבדיקה
   * @returns {boolean} האם הוא תקין
   */
  isValidPublicId(publicId) {
    if (!publicId || typeof publicId !== 'string') {
      return false;
    }
    
    // בדיקה של תווים בעייתיים
    const problematicChars = ['%', '?', '#', '&', '+'];
    if (problematicChars.some(char => publicId.includes(char))) {
      return false;
    }
    
    // בדיקה שאורך סביר
    if (publicId.length > 500 || publicId.length < 3) {
      return false;
    }
    
    return true;
  }

  /**
   * המרת public ID מהמבנה הישן למבנה החדש
   * @param {string} legacyPublicId - public ID במבנה הישן
   * @param {string} gameId - מזהה המשחק החדש
   * @returns {string} public ID במבנה החדש
   */
  convertLegacyPublicId(legacyPublicId, gameId) {
    if (!legacyPublicId || !gameId) {
      return null;
    }
    
    try {
      // אם זה כבר במבנה החדש, החזר כמו שזה
      if (legacyPublicId.startsWith(`dnd_maps/games/${gameId}/`)) {
        return legacyPublicId;
      }
      
      // אם זה במבנה הישן dnd_maps/users/{userId}/...
      if (legacyPublicId.startsWith('dnd_maps/users/')) {
        // חילוץ החלק האחרון של הנתיב
        const pathParts = legacyPublicId.split('/');
        const filename = pathParts[pathParts.length - 1];
        
        // יצירת נתיב חדש
        return `dnd_maps/games/${gameId}/${filename}`;
      }
      
      // אם זה במבנה כללי, העבר למבנה החדש
      if (legacyPublicId.startsWith('dnd_maps/')) {
        const pathParts = legacyPublicId.split('/');
        const filename = pathParts[pathParts.length - 1];
        return `dnd_maps/games/${gameId}/${filename}`;
      }
      
      // אם זה לא בפורמט המוכר, צור נתיב חדש
      const safeName = legacyPublicId.replace(/[^a-zA-Z0-9_-]/g, '_');
      return `dnd_maps/games/${gameId}/${safeName}`;
      
    } catch (error) {
      console.error('Error converting legacy public ID:', error);
      return `dnd_maps/games/${gameId}/fallback_${Date.now()}`;
    }
  }

  /**
   * יצירת thumbnail מותאם לתמונה עם טיפול ב-public IDs ישנים
   * @param {string} publicId - מזהה ציבורי של התמונה
   * @param {number} size - גודל הthumb
   * @param {string} gameId - מזהה המשחק (לטיפול במבנה ישן)
   * @returns {string} URL של הthumb
   */
  getThumbnailUrl(publicId, size = 300, gameId = null) {
    // ולידציה של ה-public ID
    if (!this.isValidPublicId(publicId)) {
      console.warn('Invalid public ID for thumbnail:', publicId);
      return null;
    }
    
    // אם יש gameId וה-public ID נראה ישן, נסה להמיר
    if (gameId && !publicId.startsWith(`dnd_maps/games/${gameId}/`)) {
      const convertedPublicId = this.convertLegacyPublicId(publicId, gameId);
      if (convertedPublicId && this.isValidPublicId(convertedPublicId)) {
        publicId = convertedPublicId;
      }
    }
    
    return this.getOptimizedImageUrl(publicId, {
      maxWidth: size,
      maxHeight: size,
      quality: 'auto:best',
      format: 'auto'
    });
  }

  /**
   * בדיקה אם URL הוא של Cloudinary
   * @param {string} url - URL לבדיקה
   * @returns {boolean} האם זה URL של Cloudinary
   */
  isCloudinaryUrl(url) {
    return url && (
      url.includes('cloudinary.com') || 
      url.includes('res.cloudinary.com')
    );
  }

  /**
   * חילוץ public ID מ-URL של Cloudinary
   * @param {string} url - URL של Cloudinary
   * @returns {string|null} Public ID או null
   */
  extractPublicId(url) {
    if (!this.isCloudinaryUrl(url)) {
      return null;
    }
    
    try {
      // נורמליזציה של ה-URL - הסרת parameters ו-fragments
      let cleanUrl = url.split('?')[0].split('#')[0];
      
      // דקודינג של תווים מקודדים
      cleanUrl = decodeURIComponent(cleanUrl);
      
      // דוגמאות לפורמטים של Cloudinary:
      // https://res.cloudinary.com/dof0vdtf3/image/upload/v1234567890/folder/image.jpg
      // https://res.cloudinary.com/dof0vdtf3/image/upload/c_scale,w_300/v1234567890/folder/image.jpg
      // https://res.cloudinary.com/dof0vdtf3/image/upload/q_auto:best/f_auto/v1/folder/image.jpg
      
      const parts = cleanUrl.split('/');
      const uploadIndex = parts.findIndex(part => part === 'upload');
      
      if (uploadIndex === -1) return null;
      
      // קבלת הנתיב אחרי /upload/
      let pathAfterUpload = parts.slice(uploadIndex + 1);
      
      // הסרת transformations - כל דבר שמתחיל ב-letter וכולל פסיק או תווים מיוחדים
      pathAfterUpload = pathAfterUpload.filter(part => {
        // אם זה transformation (כמו c_scale,w_300 או q_auto:best)
        if (part.includes(',') || part.includes(':') || part.match(/^[a-z]_/)) {
          return false;
        }
        // אם זה גירסה (vXXXXXXXXXX)
        if (part.match(/^v\d+$/)) {
          return false;
        }
        // אם זה רק v1 (גירסה כללית)
        if (part === 'v1') {
          return false;
        }
        return true;
      });
      
      // איחוד הנתיב הנקי
      const publicIdWithExtension = pathAfterUpload.join('/');
      
      // הסרת סיומת הקובץ
      const publicId = publicIdWithExtension.replace(/\.[^/.]+$/, '');
      
      // ולידציה שה-public ID נראה תקין
      if (!publicId || publicId.includes('%') || publicId.includes('?')) {
        console.warn('Extracted public ID looks malformed:', publicId, 'from URL:', url);
        return null;
      }
      
      return publicId;
      
    } catch (error) {
      console.error('Error extracting public ID from URL:', url, error);
      return null;
    }
  }

  /**
   * דחיסת תמונה לפני העלאה
   * @param {File} file - קובץ התמונה
   * @param {number} maxWidth - רוחב מקסימלי
   * @param {number} maxHeight - גובה מקסימלי
   * @param {number} quality - איכות (0-1)
   * @returns {Promise<Blob>} תמונה דחוסה
   */
  async compressImage(file, maxWidth = 2000, maxHeight = 2000, quality = 1.0) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // חישוב גודל חדש תוך שמירה על יחס גובה-רוחב
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // ציור התמונה בגודל החדש
        ctx.drawImage(img, 0, 0, width, height);
        
        // המרה ל-blob
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * קבלת תמונות של משחק לפי סוג
   * @param {string} gameId - מזהה המשחק
   * @param {string} type - סוג התמונה (אופציונלי)
   * @returns {Promise<Object>} רשימת התמונות
   */
  async getGameImages(gameId, type = null) {
    try {
      const tags = [`game_${gameId}`];
      if (type) {
        tags.push(`type_${type}`);
      }
      
      const response = await fetch(`/api/game-states/cloudinary/list-images?tags=${tags.join(',')}&gameId=${gameId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch images: ${response.statusText}`);
      }
      
      const result = await response.json();
      return {
        success: true,
        images: result.resources || [],
        total: result.total_count || 0
      };
      
    } catch (error) {
      console.error('Error fetching game images:', error);
      return {
        success: false,
        error: error.message,
        images: [],
        total: 0
      };
    }
  }
}

// יצירת instance יחיד
const cloudinaryService = new CloudinaryService();

export default cloudinaryService; 